<template>
  <programming-dashboard
    :stationName="me.company_name"
    :isStreaming="me.is_streaming"
    :afstereoDataNotFound="afstereoDataNotFound"
    :date="selectedDate"
    :messagesData="messagesData"
    :audienceProfileData="audienceProfileData"
    :contentThreadsData="contentThreadsData"
    :promotionsData="promotionsData"
    :commercialLoadData="commercialLoadData"
    :musicalLoadData="musicalLoadData"
    :topTracksData="topTracksData"
    :timeslots="activeTimeslots"
    :timeslot="selectedTimeslot"
    :activeRange="activeRange"
    :period="selectedPeriod"
    :filtersLoading="filtersLoading"
    :dataLoading="dataLoading"
    :loadingStreamingData="loadingStreamingData"
    :loadingMessage="loadingMessage"
    :loadingAudienceData="audienceDataLoading"
    :stations="stations"
    :streamingData="streamingData"
    :selectedStation="selectedStation"
    :afstereoPluginEnabled="afstereoPluginEnabled"
    :loadingPlugins="loadingPlugins"
    @onDateChange="handleDateChange"
    @onHeatMapDateChange="handleChangeDate"
    @onTimeslotChange="handleTimeslotChange"
    @onPeriodChange="handlePeriodChange"
    @onStreamingPeriodChange="handleStreamingPeriodChange"
    @onStationChange="handleStationChange"
    :selectedEngagementsFilter="selectedFilter"
    :chartsData="chartsData"
    :mainChartData="mainChartData"
    @previous="handlePrevious($event)"
    @next="handleNext($event)"
    @changeFilter="handleChangeFilter($event)"
  />
</template>

<script>
import * as analyticsTypes from '@/store/modules/analytics/mutation-types';
import ProgrammingDashboard from '@/components/analytics/ProgrammingDashboard';
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import audienceQuery from '@/graphql/analytics/audienceQuery.gql';
import programmingDashboardQuery from '@/graphql/analytics/programmingDashboardQuery.gql';
import programmingDashboardBasicQuery from '@/graphql/analytics/programmingDashboardBasicQuery.gql';
import streamingLoadQuery from '@/graphql/analytics/streamingLoadQuery.gql';

import {
  getMessageData,
  getCommercialLoadData,
  getMusicalLoadData,
  getContentThreadsData,
  getAudienceData,
  getPromotionsData,
  getTopTracksData,
} from './programmingDashboardUtils';

const numberBetween = (num, a, b) => a <= num && b >= num;

export default {
  name: 'ProgrammingDashboardContainer',
  components: {
    ProgrammingDashboard,
  },

  data: () => ({
    afstereoDataNotFound: false,
    promotionsData: [],
    commercialLoadData: [],
    musicalLoadData: [],
    messagesData: {},
    contentThreadsData: [],
    topTracksData: [],
    audienceProfileData: {},
    selectedProgrammingDate: new Date(),
    selectedStation: null,
    selectedProgrammingPeriod: 'month',
    streamingPeriod: 'week',
    dataLoading: false,
    audienceDataLoading: true,
    loadingStreamingData: false,
    streamingData: { streaming: { averageListeners: [], averageListenersTrimester: [] } },
  }),

  computed: {
    ...mapState('analytics', [
      'selectedDate',
      'selectedPeriod',
      'selectedFilter',
      'selectedTimeslot',
      'chartsData',
      'mainChartData',
    ]),
    ...mapState('user', ['me']),
    ...mapState('timeslots', ['timeslots', 'loadingTimeslots']),
    ...mapState('afstereo', ['stations']),
    ...mapGetters('timeslots', ['activeTimeslots']),
    ...mapState('plugin', ['plugins', 'loadingPlugins']),

    activeRange() {
      const nowMoment = moment(this.selectedProgrammingDate);
      const start = nowMoment.startOf(this.selectedProgrammingPeriod).format('YYYY-MM-DD');
      const end = nowMoment.endOf(this.selectedProgrammingPeriod).format('YYYY-MM-DD');

      return { start, end };
    },

    filtersLoading() {
      return this.loadingTimeslots;
    },

    loadingMessage() {
      if (this.loadingTimeslots) return 'Fetching Timeslots';
      if (this.loadingData) return 'Fetching Data';

      return 'Loading Data';
    },

    afstereoPluginEnabled() {
      return this.isPluginEnabled('app.dashboard.afstereo');
    },

    query() {
      if (this.afstereoPluginEnabled) return programmingDashboardQuery;
      return programmingDashboardBasicQuery;
    },
  },

  mounted() {
    this.$store.dispatch('timeslots/getTimeslots');
    this.$store.dispatch('afstereo/getStations');
    this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_TIMESLOT}`, {
      timeslot: 0,
    });
    this.loadCharts();
  },
  methods: {
    loadCharts() {
      this.$store.dispatch('analytics/getCharts');
      this.$store.dispatch('analytics/getMainChart');
    },
    handlePrevious(event) {
      this.$store.commit(`analytics/${analyticsTypes.SUBTRACT_ANALYTICS_DATE}`, {
        period: event.unit,
        amount: 1,
      });
      this.loadCharts();
      this.fetchDashboardData();
    },
    handleNext(event) {
      this.$store.commit(`analytics/${analyticsTypes.ADD_ANALYTICS_DATE}`, {
        period: event.unit,
        amount: 1,
      });
      this.loadCharts();
      this.fetchDashboardData();
    },
    handleChangeFilter(event) {
      if (this.selectedFilter === event.filter) return;
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_FILTER}`, {
        filter: event.filter,
      });
      this.$store.dispatch('analytics/getMainChart');
    },
    handleChangeDate(event) {
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_DATE}`, { date: event.date });
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_PERIOD}`, {
        period: event.period,
      });
      this.loadCharts();
      this.fetchDashboardData();
    },
    isPluginEnabled(pluginPermissions) {
      const permissions = this.plugins.filter(plugin => plugin.plugin_route === pluginPermissions)
        .length;
      return permissions >= 1;
    },

    handleDateChange(val) {
      this.fetchDashboardData();
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_DATE}`, { date: moment(val) });
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_PERIOD}`, {
        period: this.selectedPeriod,
      });
      this.loadCharts();
    },

    handleTimeslotChange(val) {
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_TIMESLOT}`, {
        timeslot: val,
      });
      this.loadCharts();
      this.fetchDashboardData();
    },

    handlePeriodChange(val) {
      if (this.selectedPeriod === val) return;
      this.$store.commit(`analytics/${analyticsTypes.SET_ANALYTICS_PERIOD}`, {
        period: val,
      });
      this.loadCharts();
      this.fetchDashboardData();
    },

    handleStationChange(val) {
      this.selectedStation = val;
      this.fetchDashboardData();
    },

    /**
     * Gets this first timeslot matching the current time or null if none is found
     */
    getTimeslotForCurrentDateTime() {
      const now = moment();
      const nowStr = moment().format('YYYY-MM-DD');
      const weekday = moment().isoWeekday() - 1;

      const timeslot = this.activeTimeslots.find(x => {
        if (!numberBetween(weekday, x.start_day, x.end_day)) return false;
        const start = moment(`${nowStr} ${x.start_time}`);
        const end = moment(`${nowStr} ${x.end_time}`);
        return now.isBetween(start, end, 'second', '[]');
      });

      return timeslot && timeslot.id ? timeslot.id : null;
    },

    fetchDashboardData() {
      this.dataLoading = true;
      this.audienceDataLoading = true;
      let variables = {
        timeslotId: this.selectedTimeslot,
        date: moment(this.selectedDate).format('YYYY-MM-DD'),
        unit: this.selectedPeriod,
      };

      if (this.selectedStation) {
        variables = { ...variables, playoutCompare: this.selectedStation };
      }

      this.$apollo
        .query({
          client: 'apiv2',
          query: audienceQuery,
          fetchPolicy: 'network-only',
          variables,
        })
        .then(result => {
          this.audienceProfileData = getAudienceData(result.data);
          this.audienceDataLoading = false;
        })
        .catch(() => {
          this.audienceDataLoading = false;
        });

      this.$apollo
        .query({
          query: this.query,
          fetchPolicy: 'network-only',
          variables,
        })
        .then(result => {
          this.messagesData = getMessageData(result.data.programmingDashboard);
          this.commercialLoadData = getCommercialLoadData(result.data.programmingDashboard);
          this.musicalLoadData = getMusicalLoadData(result.data.programmingDashboard);
          this.topTracksData = getTopTracksData(result.data.programmingDashboard);
          this.contentThreadsData = getContentThreadsData(result.data.programmingDashboard);
          this.promotionsData = getPromotionsData(result.data.programmingDashboard);

          this.dataLoading = false;
        })
        .catch(error => {
          this.afstereoDataNotFound = error.message.includes(
            'Station matching query does not exist.',
          );
          this.dataLoading = false;
        });
      this.fetchStreamingData();
    },

    fetchStreamingData() {
      this.loadingStreamingData = true;
      this.$apolloProvider.clients.apiv2
        .query({
          query: streamingLoadQuery,
          fetchPolicy: 'network-only',
          variables: {
            date: this.getStreamingDate().selectedDate,
            unit: this.getStreamingDate().unit,
            filter: this.streamingPeriod,
          },
        })
        .then(result => {
          this.streamingData = result.data.programmingDashboard;
          this.loadingStreamingData = false;
        })
        .catch(error => {
          this.afstereoDataNotFound = error.message.includes('Streaming data not found');
          this.loadingStreamingData = false;
        });
    },

    getStreamingDate() {
      return {
        unit: this.selectedPeriod,
        selectedDate: moment(this.selectedDate).format('YYYY-MM-DD'),
      };
    },
    handleStreamingPeriodChange(streamingPeriod) {
      this.streamingPeriod = streamingPeriod;
      this.fetchStreamingData();
    },
  },

  watch: {
    activeTimeslots() {
      this.fetchDashboardData();
    },
    plugins() {
      this.fetchDashboardData();
    },
  },
};
</script>



// WEBPACK FOOTER //
// ProgrammingDashboardContainer.vue?2066b897